import React, { useEffect, useState } from "react";
import { Modal, Spin, Checkbox, Select } from "antd";
import client from "src/api";
import { getErrorMessage, logger } from "src/lib/utils";
import { GreenTickIcon } from "../icons/green-tick-icon";

interface Props {
  isBusiness?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateTransactionModal: React.FC<Props> = ({
  isBusiness,
  onClose,
  onSuccess,
}) => {
  const [searchType, setSearchType] = useState<"id" | "email">("id");
  const [searchValue, setSearchValue] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [userName, setUserName] = useState("");
  const [currencyList, setCurrencyList] = useState<any[]>([]);
  const [currency, setCurrency] = useState<number>(0);
  const [amount, setAmount] = useState("");
  const [type, setType] = useState<"deposit" | "withdraw">("deposit");
  const [comment, setComment] = useState("");
  const [showToUser, setShowToUser] = useState(false);
  const [error, setError] = useState("");

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isOtpSubmitting, setIsOtpSubmitting] = useState(false);

  const [otp, setOtp] = useState<string>("");
  const [otpError, setOtpError] = useState("");

  useEffect(() => {
    client.manualTransfers
      .getCurrencies()
      .then((res) => {
        const list = res.data;
        setCurrencyList(list);
        if (list.length > 0) setCurrency(list[0].id);
      })
      .catch((err) => {
        console.error("Failed to load currencies", err);
        setCurrencyList([]);
      });
  }, []);

  const validateSearchValue = (): boolean => {
    if (searchType === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(searchValue)) {
        setError("Please enter a valid email");
        return false;
      }
    } else {
      if (!/^\d+$/.test(searchValue)) {
        setError("User ID must be a number");
        return false;
      }
    }
    setError("");
    return true;
  };

  const handleVerify = async () => {
    if (!validateSearchValue()) return;
    setIsVerifying(true);
    setError("");

    try {
      const res: any = await client.manualTransfers.verifyUser({
        searchBy: searchType === "id" ? "user_id" : "email",
        searchValue,
      });

      if (res?.user_id) {
        setIsVerified(true);
        setUserId(res.user_id);
        setUserName(res?.full_name);
      } else {
        setError("User not found");
      }
    } catch (e: any) {
      logger.log("EEEEEEEEEEE", e);
      const message = getErrorMessage(e.response?.data);
      setError(typeof message === "string" ? message : "Something went wrong");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = () => {
    if (!userId || !amount || !comment || !currency) {
      setError("All fields are required");
      return;
    }

    setOtp("");
    setOtpError("");
    setShowOtpModal(true);
  };

  const handleOtpSubmit = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError("Enter a valid 6-digit OTP");
      return;
    }

    setIsOtpSubmitting(true); // disable button
    setOtpError("");

    try {
      await client.manualTransfers.createManualTransaction({
        user_id: userId!,
        type,
        comment,
        asset_id: currency,
        amount: parseFloat(amount),
        is_comment_visible: showToUser,
        otp,
      });

      setShowOtpModal(false);
      onSuccess();
    } catch (e: any) {
      setOtpError(
        getErrorMessage(e.response?.data) || "Failed to create transaction"
      );
      setIsOtpSubmitting(false);
    } finally {
      setIsOtpSubmitting(false); // enable button again
    }
  };

  return (
    <>
      {/* Main Transaction Modal */}
      <Modal
        open
        onCancel={onClose}
        onOk={handleSubmit}
        okText="Create"
        okButtonProps={{
          disabled: !isVerified,
          style: {
            backgroundColor: "#1890ff", // Ant Design primary blue
            color: "#fff", // Text color white
            border: "none",
          },
        }}
        confirmLoading={isVerifying}
        title="Create Manual Transaction"
      >
        <div className="flex flex-col gap-4">
          {/* Search Type */}
          <div>
            <label>Search By</label>
            <Select
              value={searchType}
              onChange={(val) => {
                setSearchType(val);
                setSearchValue("");
                setIsVerified(false);
                setUserId(null);
                setUserName("");
              }}
              style={{ width: "100%" }}
              getPopupContainer={(triggerNode) =>
                triggerNode.parentNode as HTMLElement
              } // ensures dropdown stays inside modal
            >
              <Select.Option value="id">User ID</Select.Option>
              <Select.Option value="email">Email</Select.Option>
            </Select>
          </div>

          {/* Search Input */}
          <div>
            <label>{searchType === "id" ? "User ID" : "Email"}</label>
            <div className="relative">
              <input
                className="w-full border px-2 py-1 rounded pr-8" // note extra padding-right
                value={searchValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (searchType === "id" && !/^\d*$/.test(val)) return; // allow only numbers
                  setSearchValue(val);
                  if (isVerified) {
                    setIsVerified(false);
                    setUserId(null);
                    setUserName("");
                  }
                }}
              />
              {isVerified && (
                <GreenTickIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
              )}
            </div>

            {!isVerified && (
              <button
                className="mt-2 bg-blue-500 text-white px-3 py-1 rounded disabled:opacity-50"
                onClick={handleVerify}
                disabled={isVerifying || !searchValue}
              >
                {isVerifying ? <Spin size="small" /> : "Verify"}
              </button>
            )}
            {isVerified && (
              <div className="mt-2 px-3 py-1 rounded ">{userName}</div>
            )}
          </div>

          {/* Form fields shown only after verification */}
          {isVerified && (
            <>
              {/* Type */}
              <div>
                <label>Transaction Type</label>
                <Select
                  value={type}
                  onChange={(val) => setType(val)}
                  style={{ width: "100%" }}
                  getPopupContainer={(triggerNode) =>
                    triggerNode.parentNode as HTMLElement
                  }
                >
                  <Select.Option value="deposit">Deposit</Select.Option>
                  <Select.Option value="withdraw">Withdraw</Select.Option>
                </Select>
              </div>

              {/* Amount */}
              <div>
                <label>Amount</label>
                <input
                  type="number"
                  className="w-full border px-2 py-1 rounded"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>

              {/* Currency */}
              <div>
                <label>Currency</label>
                <Select
                  value={currency}
                  onChange={(val) => setCurrency(val)}
                  style={{ width: "100%" }}
                  getPopupContainer={(triggerNode) =>
                    triggerNode.parentNode as HTMLElement
                  }
                >
                  {currencyList.map((c: any) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.code.toUpperCase()}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              {/* Comment */}
              <div>
                <label>Comment</label>
                <textarea
                  className="w-full border px-2 py-1 rounded"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>

              {/* Show to user */}
              <div>
                <Checkbox
                  checked={showToUser}
                  onChange={(e) => setShowToUser(e.target.checked)}
                >
                  Show comment to user
                </Checkbox>
              </div>
            </>
          )}
          {/* Show error if exists */}
          {error && <div className="text-red-500 text-sm">{error}</div>}
        </div>
      </Modal>

      {/* OTP Modal - separate from main modal */}
      <Modal
        open={showOtpModal}
        title="Enter 2FA code"
        onOk={handleOtpSubmit}
        onCancel={() => setShowOtpModal(false)}
        okText="Confirm"
        okButtonProps={{
          disabled: isOtpSubmitting, // disable while submitting
          style: {
            backgroundColor: "#1890ff", // Ant Design primary blue
            color: "#fff", // Text color white
            border: "none",
          },
        }}
      >
        <div className="flex flex-col gap-3">
          <label>OTP</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoFocus
            className="w-full border px-2 py-1 rounded"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          {otpError && <div className="text-red-500 text-sm">{otpError}</div>}
        </div>
      </Modal>
    </>
  );
};

export default CreateTransactionModal;
